import { Box, DialogActions, IconButton, Typography, styled, useTheme } from "@mui/material"
import { CloseRounded } from "@mui/icons-material"

// Gradient dialog header used across the app's own HKVDialog/MUI Dialog usages, replacing a plain
// <DialogTitle>, pass an icon element and title, optionally a palette key ("primary", "error", ...).
// Pass `onClose` to also render a close button, outlined and colored to match the header (inherit)
// rather than a fixed color, omit it for dialogs that shouldn't offer this shortcut.
export const DialogHeader = ({ icon, title, color = "primary", onClose }) => {
  const theme = useTheme()
  const palette = theme.palette[color] || theme.palette.primary

  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 2,
      background: `linear-gradient(135deg, ${palette.main}, ${palette.dark})`,
      color: palette.contrastText
    }}>
      {icon}
      <Typography variant="h6" sx={{
        fontWeight: 700,
        flexGrow: 1
      }}>{title}</Typography>
      {onClose && (
        <IconButton
          onClick={onClose}
          size="small"
          color="inherit"
          sx={{
            border: "1px solid currentColor",
            opacity: 0.85,
            "&:hover": { opacity: 1, bgcolor: "rgba(255,255,255,0.15)" }
          }}
        >
          <CloseRounded fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

// Wrap a close handler for a MUI <Dialog onClose>: a click next to the dialog does nothing, so a
// dialog is left through Cancel, the cross in its header, or Escape. Half-filled forms shouldn't
// disappear on a misplaced click, and HKVDialog already behaves this way, so raw <Dialog> usages
// pass their handler through here to match.
export const dialogClose = (onClose) => (event, reason) => {
  if (reason !== "backdropClick") onClose?.()
}

// Action-button row for a dialog, separated from the content above it with a
// border, the "footer" look. Every dialog uses it, also one whose question sits
// in the header (sign-out), so the buttons are always in the same place:
// the neutral action first, the primary action last.
export const DialogFooter = styled(DialogActions)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5, 3)
}))
